import { useMemo } from "react";
import { hourMs } from "verdi-common/timeConstants";
import { useListIrrigationEvents } from "@/api/orval/generated/irrigation-event";
import { IrrigationEventResponse } from "@/api/orval/generated/schemas";
import {
    expandIrrigationDefinitions,
    filterInstancesOverlappingWindow,
    groupInstancesBySetId,
    groupInstancesByZoneId,
} from "./expand-irrigation-instances";

interface Props {
    start: Date;
    end: Date;
    /**
     * Only expand these zones. Omit to expand every active zone. An empty array expands nothing —
     * use it when the consumer has no zone selected, so we skip the RRULE walk entirely.
     * Pass a memoized array; it feeds a `useMemo` dependency.
     */
    zoneIds?: readonly string[];
}

export function useIrrigationInstances({ start, end, zoneIds }: Props) {
    const { data, isLoading, isError } = useListIrrigationEvents();

    /**
     * Expansion walks every definition's RRULE from its own dtstart, so it runs over an hour-aligned
     * window rather than the one asked for: a caller whose window slides with the clock would
     * otherwise pay the whole walk again on every tick to get the same instances back. The exact
     * window is applied to the result below. Keyed on the aligned numbers, since a fresh Date with
     * the same value is a new object and would defeat the memo.
     */
    const expansionStartMs = Math.floor(start.valueOf() / hourMs) * hourMs;
    const expansionEndMs = Math.ceil(end.valueOf() / hourMs) * hourMs;

    const expandedInstances = useMemo(
        () =>
            data?.items
                ? expandIrrigationDefinitions(data.items, new Date(expansionStartMs), new Date(expansionEndMs), zoneIds)
                : [],
        [data, expansionStartMs, expansionEndMs, zoneIds],
    );

    const instances = useMemo(
        () => filterInstancesOverlappingWindow(expandedInstances, start, end),
        [expandedInstances, start, end],
    );

    const instancesByZoneId = useMemo(() => groupInstancesByZoneId(instances), [instances]);

    /**
     * Deliberately grouped before the window filter, unlike {@link instancesByZoneId}. A set's bar spans
     * from its occurrence to whenever its last zone finishes, so a zone starting after the window ends
     * still decides where the bar stops — drop it first and the bar comes up short. `getSetEventSpans`
     * discards the spans that turn out not to overlap.
     */
    const instancesBySetId = useMemo(() => groupInstancesBySetId(expandedInstances), [expandedInstances]);

    const definitionsByRecordId = useMemo(() => {
        const byRecordId: Record<string, IrrigationEventResponse> = {};

        if (!Array.isArray(data?.items)) {
            return byRecordId;
        }

        return data.items.reduce((collector, definition) => {
            if (typeof definition.recordId !== "string") {
                // belt and suspenders guard
                return collector;
            }

            collector[definition.recordId] = definition;
            return collector;
        }, byRecordId);
    }, [data?.items]);

    return {
        instances,
        instancesByZoneId,
        instancesBySetId,
        definitionsByRecordId,
        isLoading,
        // Only a failure with nothing cached: a refetch that fails after a save still has a list to paint.
        loadFailed: isError && data?.items === undefined,
    };
}
